import {
  leadStatusIds,
  ToQueryStringFilters,
  useDate,
  useToQueryString,
} from '@/core'
import { useCatalogStore } from '@/core/store/catalogs'
import { createInjectionState, toReactive } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { TComment, TLead } from '../../../types'
import { useCatalogQuery } from '../query/useCatalogQuery'
import { useCommentsQuery } from '../query/useCommentsQuery'
import { useModuleQuery } from '../query/useModuleQuery'
import { useI18n } from 'vue-i18n'

const [useProvideLeadModule, useLeadModule] = createInjectionState(() => {
  const { zonedTimeToUtc } = useDate()
  const form = ref<TLead>({})
  const filtersStatus = ref<ToQueryStringFilters>({
    param: '',
    id: Object.values(leadStatusIds),
  })
  const projectFilters = ref<ToQueryStringFilters>({
    param: '',
    status_id: [1],
  })

  const lotFilters = ref<ToQueryStringFilters>({
    param: '',
    status_id: [25],
  })

  const store = useCatalogStore()
  const { catalogs } = storeToRefs(store)

  const statusQueryParams = useToQueryString(toReactive(filtersStatus))
  const projectsQueryParams = useToQueryString(toReactive(projectFilters), {
    searchColumns: ['name'],
  })

  const lotsQueryParams = useToQueryString(toReactive(lotFilters), {
    searchColumns: ['name'],
  })

  const route = useRoute()
  const router = useRouter()
  const { t } = useI18n()

  const isEdit = computed(() => route.name === 'leadsDetails')
  const isViewComments = computed(() => route.name === 'leadComments')
  const isCreate = computed(() => route.name === 'leadsCreate')

  const catalogQuery = useCatalogQuery({
    filters: {
      statusesCatalog: statusQueryParams.queryParams,
      projectsCatalog: projectsQueryParams.queryParams,
      leadsLotsCatalog: lotsQueryParams.queryParams,
    },
    enableQuery: {
      advisorsCatalog: computed(() => isEdit.value || isCreate.value),
      campaignsCatalog: false,
      originsCatalog: computed(() => isEdit.value || isCreate.value),
      statusesCatalog: computed(() => isEdit.value || isCreate.value),
      projectsCatalog: computed(() => isEdit.value || isCreate.value),
      lostReasonsCatalog: computed(() => isEdit.value || isCreate.value),
      lotsCatalog: false,
      socialNetworksCatalog: computed(() => isEdit.value || isCreate.value),
      referralsCatalog: computed(() => isEdit.value || isCreate.value),
      leadsLotsCatalog: computed(
        () => lotFilters.value?.project_ids?.length > 0,
      ),
    },
  })

  const moduleQuery = useModuleQuery({
    modelId: computed(() => route.params?.id?.toString()),
    enableQuery: {
      loadRecords: false,
      loadRecord: computed(
        () => !!route.params?.id && (isEdit.value || isViewComments.value),
      ),
    },
    handlers: {
      onLoadRecord: (data) => {
        form.value = data
      },
    },
  })

  const commentsQuery = useCommentsQuery({
    modelId: computed(() => Number(form.value.id)),
    enableQuery: {
      loadRecords: computed(() => !isNaN(Number(form.value.id))),
    },
  })

  const onAdd = async (payload: TLead) => {
    await moduleQuery.addRecord.mutateAsync(payload).then((lead: TLead) => {
      onRedirect(lead)
    })
  }

  const onAddComment = async (payload: TComment) => {
    await commentsQuery.addRecord.mutateAsync({
      ...payload,
      is_appointment: !!payload?.is_appointment,
      advisor_id: model.value?.advisor_id,
      ...(payload?.appointment_date
        ? {
            appointment_date: zonedTimeToUtc(
              new Date(payload?.appointment_date),
              {
                removeTimeZone: true,
              },
            ),
          }
        : {}),
    })

    await commentsQuery.loadRecords.refetch()
  }

  const onEdit = async (payload: TLead) => {
    // eslint-disable-next-line prefer-const
    let data = { ...payload }

    if (!data.email || !data.email.length) {
      delete data.email
    }

    await moduleQuery.editRecord.mutateAsync({
      ...data,
      created_at: zonedTimeToUtc(new Date(payload.created_at ?? ''), {
        removeTimeZone: false,
      }),
    })
    await moduleQuery.loadRecord.refetch()
  }

  const onRedirect = (payload: TLead) => {
    router.replace({ name: 'leadsDetails', params: { id: payload.id } })
  }

  const onRequestCatalogs = async (payload: number[]) => {
    await catalogQuery.requestCatalogs.mutateAsync({
      ids: payload,
    })
  }

  const onRefetchComments = () => {
    commentsQuery.loadRecords.refetch()
  }

  const validatePhone = async (phone: string) => {
    const response = await moduleQuery.validatePhone.mutateAsync({ phone })
    return response
  }

  const isLoading = computed(() => {
    if (isEdit.value) {
      return (
        moduleQuery.addRecord.isLoading.value ||
        moduleQuery.editRecord.isLoading.value ||
        moduleQuery.loadRecord.isLoading.value ||
        moduleQuery.loadRecord.isFetching.value ||
        commentsQuery.loadRecords.isLoading.value
      )
    }

    if (isViewComments.value) {
      return (
        moduleQuery.loadRecord.isLoading.value ||
        moduleQuery.loadRecord.isFetching.value ||
        commentsQuery.loadRecords.isLoading.value
      )
    }

    return (
      catalogQuery.socialNetworksCatalog.isLoading.value ||
      catalogQuery.advisorsCatalog.isLoading.value ||
      catalogQuery.statusesCatalog.isLoading.value ||
      catalogQuery.lostReasonsCatalog.isLoading.value
    )
  })
  const model = computed(() => {
    const data = moduleQuery.loadRecord.data.value
    if (data != undefined) {
      return {
        ...data,
        status: {
          id: data!.status!.id,
          name: t(`status.${data?.status?.name.toUpperCase()}`),
          color: data!.status!.color,
          description: data!.status!.description,
        },
      }
    } else {
      return data
    }
  })
  const comments = computed(() => commentsQuery.loadRecords.data.value)
  const isCommentsLoading = computed(
    () =>
      commentsQuery.addRecord.isLoading.value ||
      commentsQuery.loadRecords.data.value,
  )
  const advisorsCatalog = computed(
    () => catalogQuery.advisorsCatalog.data?.value,
  )

  const statusesCatalog = computed(() => {
    let data = catalogQuery.statusesCatalog.data.value
    if (!data) return []
    data = data.map((status) => {
      return {
        ...status,
        name: t(`status.${status.name?.toUpperCase()}`),
      }
    })
    return data.sort((a, b) => a.name.localeCompare(b.name))
  })

  const lostReasonsCatalog = computed(
    () => catalogQuery.lostReasonsCatalog.data?.value,
  )

  const campaignsCatalog = computed(
    () => catalogQuery.campaignsCatalog.data?.value,
  )

  const projectsCatalog = computed(
    () => catalogQuery.projectsCatalog.data?.value,
  )

  const leadsLotsCatalog = computed(
    () => catalogQuery.leadsLotsCatalog.data?.value,
  )

  const socialNetworksCatalog = computed(
    () => catalogQuery.socialNetworksCatalog.data?.value,
  )

  const referralsCatalog = computed(
    () => catalogQuery.referralsCatalog.data?.value,
  )

  return {
    form,
    isLoading,
    isCommentsLoading,
    advisorsCatalog,
    statusesCatalog,
    campaignsCatalog,
    projectsCatalog,
    leadsLotsCatalog,
    comments,
    model,
    catalogs,
    isEdit,
    lostReasonsCatalog,
    socialNetworksCatalog,
    referralsCatalog,
    lotFilters,
    validatePhone,
    onEdit,
    onAdd,
    onAddComment,
    onRequestCatalogs,
    onRefetchComments,
  }
})

export { useLeadModule, useProvideLeadModule }
